<script lang="ts">
    import {
        CheckIcon,
        PlusCircleIcon,
        MinusCircleIcon,
        ChevronDownIcon,
        SettingsIcon,
        SlidersIcon,
        XIcon,
    } from "svelte-feather-icons";
    import SvelteTooltip from "svelte-tooltip";
    import {
        calculateProsCons,
        formatDuration,
        formatMatchPercentage,
        formatMatchPercentageTooltip,
        formatNumSlopes,
        SlopeColor,
        titleCase,
    } from "../formatting";
    import BlackSquare from "../icons/BlackSquare.svelte";
    import BlueSquare from "../icons/BlueSquare.svelte";
    import GreenCircle from "../icons/GreenCircle.svelte";
    import {
        AnswerAlternatives,
        AnsweredQuestions,
        AnswerID,
        AnswerNumber,
        QuestionCollection,
        QuestionID,
        QuestionType,
        storeAnswersInLocalStorage,
    } from "../questions";
    import { QuantizedScore, SearchResult, SearchResponse, search_ws } from "../search";
    import QuestionsOverview from "./QuestionsOverview.svelte";
    import ResortDetails from "./ResortDetails.svelte";
    import { navigate, navigateBack } from "../routing/index";
    import { TransportMode } from "../transport_mode.js";
    import { Toplist, toplistCriteriaParser } from "../toplist_criteria";
    import { itemTransition, itemTransitionIn, itemTransitionOut, ListElement } from "../animations";
    import type { RouteLocation } from "../routing/route_location";
    import { onDestroy, onMount, tick } from "svelte/internal";
    import { Socket } from "../socket";
    import AnimatedList from "./AnimatedList.svelte";
    import TreeBackground from "./TreeBackground.svelte";
    import LoadingSpinner from "./LoadingSpinner.svelte";
    import { Any, dict, Re, S, seq, union } from "../combinators";
    import {
        RouteResort,
        RouteSearch,
        RouteSettings,
        RouteToplist,
        urlToString,
        URLComponents,
    } from "../search_routing";

    export let location: RouteLocation;

    let questions: QuestionCollection | null = null;

    let urlParams = new URLSearchParams(location.search);
    const DefaultLimit = 18;
    let answered: AnsweredQuestions = new AnsweredQuestions();

    let animatedList: AnimatedList<SearchResult> | null = null;
    let renderedItems: ListElement<SearchResult>[] = [];

    let searchResults: SearchResponse | null = null;
    let origin_id: number | null = null;
    let origin_name: string | null = null;
    let transport_mode: TransportMode | null = null;
    const defaultTitle = "Fjälltoppen";
    const defaultDescription =
        "Hitta bästa skidorten för just dig! Här kan du enkelt jämföra Sveriges alla skidbackar utifrån restid, familjevänlighet, offpistmöjliheter...";
    let title = defaultTitle;
    let description = defaultDescription;
    const socketProtocol = window.location.protocol == "https:" ? "wss:" : "ws:";
    let socket = new Socket(`${socketProtocol}//${window.location.host}/socket`);
    let lastSearchItemUpdate = performance.now();
    let topGroupingHeight = 800;

    socket.send(JSON.stringify({ type: "questions" })).then((s) => {
        const data = JSON.parse(s);
        if (data.type != "questions") {
            throw `Unexpected message type ${data.type}`;
        }
        questions = data.data as QuestionCollection;
        updateAnsweredQuestionsFromUrl(true);
    });

    export let parsedUrl: RouteToplist | RouteResort | RouteSearch | RouteSettings;

    const updateSearchResults = (newResults: SearchResponse) => {
        searchResults = newResults;
        lastSearchItemUpdate = performance.now();
        animateTopGrouping();
    };

    function mean(values: number[]): number {
        if (values.length == 0) throw "empty array";
        let sum = 0;
        for (let i = 0; i < values.length; i++) sum += values[i];
        return sum / values.length;
    }

    const animateTopGrouping = async () => {
        // Wait until the animateList function is done tweaking the layout and animations
        await tick();
        await tick();

        if (animatedList === null) return;

        let searchResultsElement = animatedList.getListElement();
        if (renderedItems.length == 0) return;

        // Note: Must be synced with the @media queries in the CSS
        const columns = window.innerWidth <= 800 ? 1 : window.innerWidth <= 1300 ? 2 : 3;
        const rows = Math.ceil(6 / columns);

        let top = searchResultsElement.getBoundingClientRect().top;
        let bottom = mean(
            renderedItems
                .slice(columns * (rows - 1), columns * rows)
                .map((x) => x.element!.getBoundingClientRect().bottom)
        );
        topGroupingHeight = bottom - top;

        // Animate for a second after updates
        if (performance.now() < lastSearchItemUpdate + 1200) {
            requestAnimationFrame(animateTopGrouping);
        }
    };

    const onResize = () => {
        animateTopGrouping();
    };

    onMount(() => {
        window.addEventListener("resize", onResize);
    });

    onDestroy(() => {
        window.removeEventListener("resize", onResize);
    });

    const refreshUrl = () => {
        let newUrl = { ...parsedUrl };
        if (newUrl.type == "toplist") {
            newUrl = {
                type: "search",
            };
        }

        navigate(urlToString(newUrl) + answered.toQueryString(), { replace: true });
    };

    const toplistTitle = (criteria: RouteToplist) => {
        let criteriaDescription: string | null = null;
        switch (criteria?.property) {
            case Toplist.OffPiste:
                criteriaDescription = "Bästa offpisten";
                break;
            case Toplist.BeginnerFriendly:
                criteriaDescription = "Bästa skidorterna för nybörjare";
                break;
            case Toplist.ChildFriendly:
                criteriaDescription = "Bästa barnvänliga skidorterna";
                break;
            case Toplist.ToughSlopes:
                criteriaDescription = "Bästa utmanande skidorterna";
                break;
            case Toplist.LongWindingSlopes:
                criteriaDescription = "Bästa skidorterna med långa backar";
                break;
            case Toplist.Park:
                criteriaDescription = "Bästa skiparken";
                break;
            case Toplist.CrossCountry:
                criteriaDescription = "Bästa skidorterna med längdskidor";
                break;
            case Toplist.AfterSki:
                criteriaDescription = "Bästa Afterskin";
                break;
            default:
                criteriaDescription = "Bästa skidorterna";
        }
        let locationDescription: string | null = null;
        if (!criteria?.location || criteria?.location == "sverige") {
            locationDescription = "i Sverige";
        } else {
            locationDescription = `nära ${origin_name}`;
        }
        return `${criteriaDescription} ${locationDescription}`;
    };

    const refreshTitle = () => {
        let newTitle: string = defaultTitle;
        if (active_detail_view) {
            newTitle = `${active_detail_view.name} - ${defaultTitle}`;
        } else if (parsedUrl.type == "toplist") {
            newTitle = `${toplistTitle(parsedUrl)} - ${defaultTitle}`;
        }
        title = newTitle;
    };

    const refreshDescription = () => {
        let newDescription: string = "";
        if (active_detail_view) {
            newDescription = active_detail_view.description;
        } else if (searchResults && searchResults.results.length >= 3) {
            if (parsedUrl.type == "toplist") {
                let criteriaDescription: string | null = null;
                switch (parsedUrl.property) {
                    case Toplist.OffPiste:
                        criteriaDescription = "Det här är skidorterna med den bästa offpisten";
                        break;
                    case Toplist.BeginnerFriendly:
                        criteriaDescription = "Det här är de bästa nybörjarvänliga skidorterna";
                        break;
                    case Toplist.ChildFriendly:
                        criteriaDescription = "Det här är de bästa barnvänliga skidorterna";
                        break;
                    case Toplist.ToughSlopes:
                        criteriaDescription = "Det här är de bästa utmanande skidorterna";
                        break;
                    case Toplist.LongWindingSlopes:
                        criteriaDescription = "Det här är de bästa skidorterna med långa backar";
                        break;
                    case Toplist.Park:
                        criteriaDescription = "Det här är skidorterna med bäst skipark";
                        break;
                    case Toplist.CrossCountry:
                        criteriaDescription =
                            "Det här är de bästa skidorterna för dig som vill åka både utför och på längden";
                        break;
                    case Toplist.AfterSki:
                        criteriaDescription = "Det här är skidorterna med den bästa afterskin";
                        break;
                    default:
                        criteriaDescription = "Det här är den bästa skidorterna";
                }
                let locationDescription: string | null = null;
                if (!parsedUrl.location || parsedUrl.location == "sverige") {
                    locationDescription = "i Sverige";
                } else {
                    locationDescription = `nära ${origin_name}`;
                }
                newDescription = `${criteriaDescription} ${locationDescription}:`;
            } else {
                newDescription = `Det här är de bästa skidorterna för dig:`;
            }
            for (let i = 0; i < Math.min(searchResults.results.length, 10); i += 1) {
                newDescription += ` • ${searchResults.results[i].name}`;
            }
        }
        description = newDescription;
    };

    const refresh = () => {
        console.log("Searching " + performance.now());
        search_ws(socket, answered).then(updateSearchResults);

        const origin_quesion_answer = answered.tryGetByID(QuestionID.Origin)?.answer;
        if (origin_quesion_answer instanceof AnswerNumber) {
            origin_id = origin_quesion_answer.selected;
            if (questions) {
                const origin_question = questions.questions.find((option) => option.id == QuestionID.Origin);
                if (origin_question && origin_question.type == QuestionType.Autocomplete) {
                    origin_name =
                        origin_question.alternatives.find((alternative) => alternative.id == origin_id)?.label || null;
                }
            }
        }

        const transport_mode_answer = answered.tryGetByID(QuestionID.Transportation)?.answer;
        if (transport_mode_answer instanceof AnswerAlternatives) {
            let new_transport_mode: TransportMode | null = null;
            if (transport_mode_answer.selected.has(AnswerID.Car)) {
                new_transport_mode = TransportMode.Car;
            } else if (transport_mode_answer.selected.has(AnswerID.PublicTransport)) {
                new_transport_mode = TransportMode.PublicTransport;
            }
            transport_mode = new_transport_mode;
        }

        limit = DefaultLimit;
    };

    const updateAnsweredQuestionsFromUrl = (forceRefresh: boolean) => {
        if (questions) {
            let newAnswered: AnsweredQuestions | null;
            if (parsedUrl.type == "toplist") {
                newAnswered = AnsweredQuestions.fromTopListUri(parsedUrl, questions);
                newAnswered?.addDefaultAnswers(questions);
            } else {
                newAnswered = AnsweredQuestions.fromURLParams(new URLSearchParams(location.search), questions);
                newAnswered.addDefaultAnswers(questions);
            }
            if (newAnswered && (!newAnswered.equals(answered) || forceRefresh)) {
                answered = newAnswered;
                refresh();
            }
        }
    };

    const navigateToResort = (resortId: string) => {
        if (plausible) {
            plausible("Open resort details", { props: { resortId } });
        }
        if (searchResults) {
            // Let the server know that we selected this resort
            fetch("/resortdetails?resort=" + resortId + "&query=" + searchResults.id);
        }

        if (parsedUrl.type === "toplist") {
            navigate(
                urlToString({
                    ...parsedUrl,
                    resort: resortId,
                }) + answered.toQueryString()
            );
        } else {
            let url =
                urlToString({
                    type: "resort",
                    resort: resortId,
                }) + answered.toQueryString();
            navigate(url);
        }
        if (innerWidth < 800) {
            // TODO: Figure out a way to return to the correct scroll position in the results list when clicking the back button.
            window.scrollTo(0, 0);
        }
    };

    const navigateToSearch = () => {
        if (parsedUrl.type == "toplist") {
            navigateBack(
                urlToString({
                    ...parsedUrl,
                    resort: null,
                })
            );
        } else {
            storeAnswersInLocalStorage(answered);
            navigateBack(
                urlToString({
                    type: "search",
                }) + answered.toQueryString()
            );
        }
    };

    const findLocation = (items: SearchResult[], resort: string): SearchResult | null => {
        return items?.find((x) => x.id == resort) || null;
    };
    let active_detail_view: SearchResult | null = null;
    let limit: number = DefaultLimit;

    $: {
        // let r = parseUrl(location);
        // if (r == null) navigate("/404");
        // else parsedUrl = r;

        updateAnsweredQuestionsFromUrl(false);
        const resortId = parsedUrl.type == "toplist" || parsedUrl.type == "resort" ? parsedUrl.resort : null;
        if (resortId && searchResults !== null) {
            active_detail_view = findLocation(searchResults.results, resortId);
        } else {
            active_detail_view = null;
        }

        refreshTitle();
        refreshDescription();
    }

    let innerWidth: number = 0;
    $: anyOverlayActive =
        parsedUrl.type == "settings" ||
        parsedUrl.type == "resort" ||
        (parsedUrl.type == "toplist" && parsedUrl.resort !== null);
</script>

<svelte:window bind:innerWidth />

<main id="search-results-main">
    <div class="mobile-menubar">
        <div
            class="settings"
            class:visible={!anyOverlayActive}
            on:click={() => {
                if (anyOverlayActive) {
                    navigateToSearch();
                } else if (parsedUrl.type != "settings") {
                    navigate(
                        urlToString({
                            type: "settings",
                            parent: { type: "search" },
                        }) + answered.toQueryString()
                    );
                }
            }}
        >
            <XIcon class={anyOverlayActive ? "visible" : ""} />
            <SlidersIcon class={!anyOverlayActive ? "visible" : ""} />
        </div>
    </div>

    {#if innerWidth <= 800 && active_detail_view !== null}
        <ResortDetails
            location={active_detail_view}
            {origin_id}
            {origin_name}
            {transport_mode}
            {answered}
            {urlParams}
            fullscreen={true}
            onClose={() => {
                navigateToSearch();
            }}
        />
    {:else}
        <TreeBackground />

        <div class="questions-wrapper" class:visible={parsedUrl.type == "settings"}>
            {#if questions !== null && searchResults !== null}
                <QuestionsOverview
                    collection={questions}
                    resorts={searchResults.results}
                    bind:answered
                    onSave={() => {
                        navigateToSearch();
                        window.scrollTo({ top: 0 });
                    }}
                    onChange={() => {
                        refreshUrl();
                        refresh();
                    }}
                />
            {/if}
        </div>
        {#if searchResults === null}
            <div class="search-progress">
                <LoadingSpinner />
                <span>Söker efter de bästa skidorterna för dig...</span>
            </div>
        {:else}
            <div class="search-results-wrapper">
                <AnimatedList
                    className="search-results"
                    items={searchResults.results.slice(0, limit)}
                    transition={itemTransition}
                    transitionIn={itemTransitionIn}
                    transitionOut={itemTransitionOut}
                    bind:renderedItems
                    bind:this={animatedList}
                >
                    {#if innerWidth <= 800}
                        <li class="top-header top-banner">
                            {#if parsedUrl.type == "toplist"}
                                {toplistTitle(parsedUrl)}
                            {:else}
                                Här är bästa skidorterna för dig
                            {/if}
                        </li>
                    {:else}
                        <li class="top-header top-grouping" style={`height: ${topGroupingHeight}px`}>
                            <h3>
                                {#if parsedUrl.type == "toplist"}
                                    {toplistTitle(parsedUrl)}
                                {:else}
                                    Bästa skidorterna för dig
                                {/if}
                            </h3>
                            <div class="grouping-border" />
                        </li>
                    {/if}

                    {#each renderedItems as location, index (location.data.id)}
                        <li on:click={() => navigateToResort(location.data.id)} bind:this={location.element}>
                            <div class="item-header">
                                <h2>{location.data.name}</h2>
                                <span class="match-score-container">
                                    <!-- <SvelteTooltip
                                        tip={formatMatchPercentageTooltip(location.data.matching_percentage)}
                                        bottom
                                        color="#2289d3"
                                    > -->
                                    <span class="match-score"
                                        >{formatMatchPercentage(location.data.matching_percentage)}</span
                                    >
                                    <!-- </SvelteTooltip> -->
                                </span>
                            </div>
                            {#if location.data.image_url}
                                <!-- svelte-ignore a11y-missing-attribute -->
                                <img src={location.data.image_url} />
                            {/if}
                            {#if location.data.description}
                                <p>{location.data.description}</p>
                            {:else}
                                <p><i>Ingen beskrivning</i></p>
                            {/if}
                            <hr />
                            <div class="middle-info">
                                <div class="slope-counts">
                                    <!-- <SvelteTooltip
                                        tip={formatNumSlopes(location.data.num_green_runs, SlopeColor.Green)}
                                        bottom
                                        color="#199608"
                                    > -->
                                    <span class="slope-count slope-green"
                                        >{location.data.num_green_runs !== null
                                            ? location.data.num_green_runs
                                            : "?"}<GreenCircle /></span
                                    >
                                    <!-- </SvelteTooltip> -->
                                    <!-- <SvelteTooltip
                                        tip={formatNumSlopes(location.data.num_blue_runs, SlopeColor.Blue)}
                                        bottom
                                        color="#2289d3"
                                    > -->
                                    <span class="slope-count slope-blue"
                                        >{location.data.num_blue_runs !== null
                                            ? location.data.num_blue_runs
                                            : "?"}<BlueSquare /></span
                                    >
                                    <!-- </SvelteTooltip>
                                    <SvelteTooltip
                                        tip={formatNumSlopes(location.data.num_red_runs, SlopeColor.Red)}
                                        bottom
                                        color="#d32822"
                                    > -->
                                    <span class="slope-count slope-red"
                                        >{location.data.num_red_runs !== null
                                            ? location.data.num_red_runs
                                            : "?"}<BlueSquare /></span
                                    >
                                    <!-- </SvelteTooltip>
                                    <SvelteTooltip
                                        tip={formatNumSlopes(location.data.num_black_runs, SlopeColor.Black)}
                                        bottom
                                        color="#000000"
                                    > -->
                                    <span class="slope-count slope-black"
                                        >{location.data.num_black_runs !== null
                                            ? location.data.num_black_runs
                                            : "?"}<BlackSquare /></span
                                    >
                                    <!-- </SvelteTooltip> -->
                                </div>
                                {#if transport_mode && origin_id && location.data.travel_time_in_seconds !== null}
                                    <span
                                        class="distance"
                                        class:outside_range={!location.data.travel_time_within_range}
                                    >
                                        <!-- svelte-ignore a11y-missing-attribute -->
                                        {#if transport_mode == TransportMode.Car}
                                            <img src="/static/car.svg" />
                                        {:else}
                                            <img src="/static/train.svg" />
                                        {/if}
                                        <div>
                                            {formatDuration(location.data.travel_time_in_seconds)}
                                        </div>
                                    </span>
                                {/if}
                            </div>
                            <ul
                                style={`height: ${
                                    21 * Math.ceil(calculateProsCons(location.data, answered).length / 2)
                                }px;`}
                                class="pros-cons"
                            >
                                {#each calculateProsCons(location.data, answered) as item (item.description)}
                                    <li class={item.score == QuantizedScore.BAD ? "negative" : "positive"}>
                                        {#if item.score == QuantizedScore.BAD}
                                            <MinusCircleIcon />
                                        {:else}
                                            <PlusCircleIcon />
                                        {/if}
                                        <span>{item.description}</span>
                                    </li>
                                {/each}
                            </ul>
                        </li>
                    {/each}
                </AnimatedList>
                {#if searchResults != null && limit < searchResults.results.length}
                    <button
                        class="showmore"
                        on:click={() => {
                            limit *= 2;
                            if (searchResults !== null) updateSearchResults(searchResults);
                        }}
                    >
                        Visa fler
                        <ChevronDownIcon />
                    </button>
                {/if}
            </div>
        {/if}
        {#if active_detail_view !== null}
            <ResortDetails
                location={active_detail_view}
                {origin_id}
                {origin_name}
                {transport_mode}
                {answered}
                {urlParams}
                fullscreen={false}
                onClose={() => {
                    navigateToSearch();
                }}
            />
        {/if}
    {/if}
</main>

<svelte:head>
    <title>{title}</title>
    <meta name="description" content={description} />
</svelte:head>

<style>
</style>
